import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import {
  DatesSetArg,
  DayCellMountArg,
  EventMountArg,
  EventContentArg,
  EventInput,
  MoreLinkContentArg,
} from '@fullcalendar/core'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  ButtonBase,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { styled, Theme } from '@mui/material/styles'
import { differenceInCalendarDays, format, isSameDay, isSameMonth } from 'date-fns'

import { i18WithDefault as t } from '@shared/locale'
import { getInvoiceSubStatusColors, TInvoiceSubStatusColors } from 'components-ui/src/shared/invoices'
import { ServiceTypeIcon } from 'components-ui/src/icon_avatar/icon_avatar'

import { dayToJSDate, getSplitDate } from 'service-api/src/shared/dates'
import { getCustomInvoicePetNames } from 'service-api/src/mappers'
import { TCustomInvoiceDto } from 'service-api/src/types/general'

export interface BookingCalendarProps {
  accountName: string
  invoices: TCustomInvoiceDto[]
  onVisibleRangeChange?: (start: Date, end: Date, focusDate?: Date) => void
  initialDate?: Date
  date?: Date
  headerAction?: ReactNode
}

interface BookingEventExtendedProps {
  invoice: TCustomInvoiceDto
  bgColor: string
  color: string
  isMultiDay: boolean
}

const MOBILE_MAX_EVENTS_PER_DAY = 2

const CalendarWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  '.fc .fc-daygrid-day.fc-day-today': {
    backgroundColor: theme.palette.action.hover,
  },
  '.fc .fc-event': {
    border: 0,
    backgroundColor: 'transparent',
  },
  '.fc-event-main': {
    padding: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  '.fc-daygrid-event.booking-event--single-day.fc-event-start.fc-event-end': {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  '.fc-daygrid-day-frame': {
    padding: theme.spacing(0.5),
  },
  '.fc-daygrid-day-bottom': {
    textAlign: 'center',
  },
  '.fc-daygrid-more-link': {
    display: 'block',
    float: 'none',
    color: theme.palette.text.secondary,
  },
}))

const getEventColors = (theme: Theme, invoice: TCustomInvoiceDto): TInvoiceSubStatusColors => {
  const colors = getInvoiceSubStatusColors(theme, invoice)
  return {
    backgroundColor: colors.backgroundColor || theme.palette.action.hover,
    color: colors.color || theme.palette.action.hover,
  }
}

const toDay = (value: string): Date => dayToJSDate(getSplitDate(value)[0])!

const isMultiDayStay = (invoice: TCustomInvoiceDto): boolean => {
  if (!invoice.startDate || !invoice.endDate) return false
  return differenceInCalendarDays(toDay(invoice.endDate), toDay(invoice.startDate)) > 1
}

// Multi-day stays occupy start (inclusive) through end (exclusive)
const invoiceCoversDay = (invoice: TCustomInvoiceDto, day: Date): boolean => {
  if (!invoice.startDate) return false
  const start = toDay(invoice.startDate)
  if (isSameDay(start, day)) return true
  if (!isMultiDayStay(invoice) || !invoice.endDate) return false
  return differenceInCalendarDays(day, start) > 0 && differenceInCalendarDays(toDay(invoice.endDate), day) > 0
}

const getInvoiceUrl = (accountName: string, invoice: TCustomInvoiceDto) =>
  `/${accountName}/account/bookings/${invoice.orderId}`

const getTimeRange = (invoice: TCustomInvoiceDto): string =>
  [invoice.startTime, invoice.endTime].filter(Boolean).join(' - ')

const ICON_BLOCK_HEIGHT = 20
const DETAILED_BLOCK_HEIGHT = 48
const DESKTOP_ARROW_DEPTH = 18
const MOBILE_ARROW_DEPTH = 8

// Pointed edge built from CSS borders
const ArrowEdge = ({
  side,
  color,
  halfHeight,
  depth,
}: {
  side: 'left' | 'right'
  color: string
  halfHeight: number
  depth: number
}) => (
  <Box
    aria-hidden
    data-arrow={side}
    sx={{
      position: 'absolute',
      top: 0,
      [side]: 0,
      width: 0,
      height: 0,
      borderTop: `${halfHeight}px solid transparent`,
      borderBottom: `${halfHeight}px solid transparent`,
      ...(side === 'right'
        ? { borderLeft: `${depth}px solid ${color}` }
        : { borderRight: `${depth}px solid ${color}` }),
    }}
  />
)

const withEdges = (
  block: ReactElement,
  height: number,
  bgColor: string,
  continuesBefore: boolean,
  continuesAfter: boolean,
  depth: number
) => (
  <Box
    sx={{
      position: 'relative',
      width: '100%',
      mb: height === ICON_BLOCK_HEIGHT ? 0.5 : 0.75,
      pl: continuesBefore ? `${depth}px` : 0,
      pr: continuesAfter ? `${depth}px` : 0,
    }}
  >
    {block}
    {continuesBefore && <ArrowEdge side="left" color={bgColor} halfHeight={height / 2} depth={depth} />}
    {continuesAfter && <ArrowEdge side="right" color={bgColor} halfHeight={height / 2} depth={depth} />}
  </Box>
)

interface EventDetailsBlockProps {
  invoice: TCustomInvoiceDto
  bgColor: string
  color: string
  isCompact?: boolean
  showHoverDetails?: boolean
  showTime?: boolean
  showStatus?: boolean
  // The stay started in an earlier week
  continuesBefore?: boolean
  // The stay continues into the next week
  continuesAfter?: boolean
}

const EventDetailsBlock = ({
  invoice,
  bgColor,
  color,
  isCompact = false,
  showHoverDetails = false,
  showTime = false,
  showStatus = false,
  continuesBefore = false,
  continuesAfter = false,
}: EventDetailsBlockProps) => {
  const timeRange = getTimeRange(invoice)
  const petNames = getCustomInvoicePetNames(invoice)
  const serviceType = invoice.serviceTypeName ?? undefined

  const hoverCard = (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        p: 2,
        minWidth: 200,
        borderRadius: 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 4,
      }}
    >
      <ServiceTypeIcon
        serviceType={serviceType}
        fill="#000"
        sx={{ width: 24, height: 24, p: 0.35, fill: '#000', backgroundColor: bgColor, borderRadius: '50%' }}
      />
      <Stack direction="column" gap={0.2}>
        <Typography variant="body1bold" color="inherit">
          {invoice.service}
        </Typography>
        {invoice.serviceDate && (
          <Typography variant="body3" color="inherit">
            {invoice.serviceDate}
            {timeRange && ` | ${timeRange}`}
          </Typography>
        )}
        {petNames && (
          <Typography variant="body3" color="inherit">
            {petNames}
          </Typography>
        )}
      </Stack>
    </Stack>
  )

  const withHover = (node: ReactElement) =>
    showHoverDetails ? (
      <Tooltip
        title={hoverCard}
        placement="bottom"
        describeChild
        enterDelay={35}
        slotProps={{ tooltip: { sx: { p: 0, m: 0, maxWidth: 'none', bgcolor: 'transparent' } } }}
      >
        {node}
      </Tooltip>
    ) : (
      node
    )

  if (isCompact || continuesBefore) {
    const iconHeight = isCompact ? ICON_BLOCK_HEIGHT : DETAILED_BLOCK_HEIGHT

    return withHover(
      withEdges(
        <Box
          aria-label={invoice.service ?? undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: iconHeight,
            borderRadius: 1,
            borderLeft: continuesBefore ? 'none' : `2px solid ${color}`,
            backgroundColor: bgColor,
            width: '100%',
          }}
        >
          <ServiceTypeIcon serviceType={serviceType} fill="#000" sx={{ ml: 0.25, fontSize: 15, fill: '#000' }} />
        </Box>,
        iconHeight,
        bgColor,
        continuesBefore,
        continuesAfter,
        isCompact ? MOBILE_ARROW_DEPTH : DESKTOP_ARROW_DEPTH
      )
    )
  }

  const CalendarEventBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
        ml: 0,
        px: 1,
        height: DETAILED_BLOCK_HEIGHT,
        borderRadius: 1,
        borderLeft: `2px solid ${color}`,
        backgroundColor: bgColor,
      }}
    >
      <ServiceTypeIcon
        serviceType={serviceType}
        fill="#000"
        sx={{ width: 16, height: 16, flexShrink: 0, fill: '#000' }}
      />

      <Stack
        sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack sx={{ minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="body3bold" noWrap color="text.primary">
            {invoice.service}
          </Typography>

          {petNames && (
            <Typography variant="body3" noWrap color="text.primary">
              {petNames}
            </Typography>
          )}
        </Stack>

        {(showTime || showStatus) && (
          <Stack sx={{ flexShrink: 0, alignItems: 'flex-end' }}>
            {showStatus && (
              <Typography variant="body3bold" noWrap sx={{ color }}>
                {t(`calendar.status.${invoice.invoiceSubStatus ?? 'REQUESTED'}`)}
              </Typography>
            )}

            {showTime && timeRange && (
              <Typography variant="body3" noWrap color="text.primary">
                {timeRange}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  )

  return withHover(
    withEdges(CalendarEventBlock, DETAILED_BLOCK_HEIGHT, bgColor, continuesBefore, continuesAfter, DESKTOP_ARROW_DEPTH)
  )
}

export const BookingCalendar = ({
  accountName,
  invoices,
  onVisibleRangeChange,
  initialDate,
  date,
  headerAction,
}: BookingCalendarProps) => {
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const calendarRef = useRef<FullCalendar | null>(null)

  const [focusDate, setFocusDate] = useState<Date>(() => initialDate ?? new Date())
  const [selectedDay, setSelectedDay] = useState<Date | null>(null)

  const bookingEvents: EventInput[] = useMemo(
    () =>
      invoices
        .filter((invoice) => invoice.startDate)
        .map((invoice, index) => {
          const isMultiDay = isMultiDayStay(invoice)
          const { backgroundColor, color } = getEventColors(theme, invoice)

          return {
            id: invoice.id ?? invoice.orderId ?? `invoice-${index}`,
            title: invoice.service ?? '',
            url: getInvoiceUrl(accountName, invoice),
            start: invoice.startDate,
            end: isMultiDay ? invoice.endDate : undefined,
            display: 'block',
            classNames: isMultiDay ? [] : ['booking-event--single-day'],
            extendedProps: {
              invoice,
              bgColor: backgroundColor,
              color,
              isMultiDay,
            } satisfies BookingEventExtendedProps,
          }
        }),
    [invoices, theme, accountName]
  )

  const selectedDayInvoices = useMemo(
    () => (selectedDay ? invoices.filter((invoice) => invoiceCoversDay(invoice, selectedDay)) : []),
    [invoices, selectedDay]
  )

  const goPrev = () => calendarRef.current?.getApi().prev()
  const goNext = () => calendarRef.current?.getApi().next()
  const goToday = () => calendarRef.current?.getApi().today()

  useEffect(() => {
    const api = calendarRef.current?.getApi()
    if (date && api && !isSameMonth(api.getDate(), date)) api.gotoDate(date)
  }, [date])

  const onDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setFocusDate(arg.view.currentStart)
      onVisibleRangeChange?.(arg.start, arg.end, arg.view.currentStart)
    },
    [onVisibleRangeChange]
  )

  const openEventsInNewTab = useCallback((arg: EventMountArg) => {
    arg.el.setAttribute('target', '_blank')
  }, [])

  const isMobileRef = useRef(isBelowMD)
  isMobileRef.current = isBelowMD

  // FullCalendar's dateClick needs the interaction plugin, so attach a click listener per day cell.
  const onDayCellDidMount = useCallback((arg: DayCellMountArg) => {
    arg.el.addEventListener('click', (event) => {
      if (!isMobileRef.current) return
      if ((event.target as HTMLElement).closest('.fc-event')) return
      setSelectedDay(arg.date)
    })
  }, [])

  // Suppresses FullCalendar's popover. The day comes from the cell's click listener above: the link's own
  // date is the start of its earliest hidden event, which can be an earlier day for a multi-day stay.
  const onMoreLinkClick = useCallback(() => 'none' as const, [])

  const onMoreLinkContent = useCallback((arg: MoreLinkContentArg) => arg.shortText, [])

  const renderEvent = useCallback(
    (arg: EventContentArg) => {
      const { invoice, bgColor, color, isMultiDay } = arg.event.extendedProps as BookingEventExtendedProps
      return (
        <EventDetailsBlock
          invoice={invoice}
          bgColor={bgColor}
          color={color}
          showTime={isMultiDay}
          isCompact={isBelowMD}
          showHoverDetails={!isBelowMD}
          continuesBefore={isMultiDay && !arg.isStart}
          continuesAfter={isMultiDay && !arg.isEnd}
        />
      )
    },
    [isBelowMD]
  )

  return (
    <CalendarWrapper>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} my={2} px={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Button sx={{ minWidth: 32, p: 0 }} onClick={goPrev} aria-label={t('calendar.previous')}>
            <KeyboardArrowLeftIcon sx={{ fontSize: 32 }} />
          </Button>

          <Button variant="outlined" size="small" onClick={goToday}>
            {t('calendar.today')}
          </Button>

          <Button sx={{ minWidth: 32, p: 0 }} onClick={goNext} aria-label={t('calendar.next')}>
            <KeyboardArrowRightIcon sx={{ fontSize: 32 }} />
          </Button>

          <Typography variant="body1bold" sx={{ whiteSpace: 'nowrap', ml: 1 }}>
            {format(focusDate, 'MMMM yyyy')}
          </Typography>
        </Stack>
        {headerAction}
      </Stack>

      <Box px={0.75}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          initialDate={initialDate}
          headerToolbar={false}
          events={bookingEvents}
          height="auto"
          dayMaxEvents={isBelowMD ? MOBILE_MAX_EVENTS_PER_DAY : undefined}
          moreLinkClick={isBelowMD ? onMoreLinkClick : undefined}
          moreLinkContent={isBelowMD ? onMoreLinkContent : undefined}
          dayCellDidMount={onDayCellDidMount}
          datesSet={onDatesSet}
          eventDidMount={openEventsInNewTab}
          eventContent={renderEvent}
        />
      </Box>

      <Drawer
        anchor="bottom"
        open={!!selectedDay}
        onClose={() => setSelectedDay(null)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '70vh' } }}
      >
        <Box p={2} pb={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{selectedDay && format(selectedDay, 'EEEE, MMM d')}</Typography>
            <IconButton aria-label={t('calendar.close')} onClick={() => setSelectedDay(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack spacing={1}>
            {selectedDayInvoices.map((invoice) => {
              const { backgroundColor, color } = getEventColors(theme, invoice)

              return (
                <ButtonBase
                  key={invoice.id ?? invoice.orderId}
                  component="a"
                  href={getInvoiceUrl(accountName, invoice)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setSelectedDay(null)}
                  sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1 }}
                >
                  <EventDetailsBlock invoice={invoice} bgColor={backgroundColor} color={color} showTime showStatus />
                </ButtonBase>
              )
            })}
          </Stack>
        </Box>
      </Drawer>
    </CalendarWrapper>
  )
}
